import { useState, useEffect, useRef } from "react";

export function useSmoothedValue<T>(
  value: T,
  delay: number = 300,
  shouldDelay: (newValue: T, oldValue: T) => boolean,
  serialize: (val: T) => string = val => JSON.stringify(val)
): T {
  const [confirmedValue, setConfirmedValue] = useState<T>(value);
  const valueHash = serialize(value);
  const confirmedHashRef = useRef(valueHash);
  const previousValueRef = useRef(value);

  // 1. Determine if we are logically in a "pending" state.
  // We compare the incoming value's hash with our last "confirmed" hash.
  const isPendingDelay = valueHash !== confirmedHashRef.current && shouldDelay(value, previousValueRef.current);

  useEffect(() => {
    // If we've already synchronized, do nothing.
    if (valueHash === confirmedHashRef.current) return;

    const oldValue = previousValueRef.current;
    previousValueRef.current = value;

    // FAST-ON: If we shouldn't delay, synchronize immediately.
    if (!shouldDelay(value, oldValue)) {
      confirmedHashRef.current = valueHash;
      setConfirmedValue(value);
      return;
    }

    // SLOW-OFF: Otherwise, hold the old state for a moment.
    const handler = setTimeout(() => {
      confirmedHashRef.current = valueHash;
      setConfirmedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [valueHash, delay]);

  // If we are pending a delay, return the old confirmed value.
  // Otherwise, return the new value (providing instant updates for non-delayed changes).
  return isPendingDelay ? confirmedValue : value;
}
